// "Properties - Illustrative Projects": ready-made Properties projects - textbook models (Haldane, Kane-Mele, graphene
// with fields, the MoS2 three-band model, a cosine band) with their variations, and the fits of the Materials
// illustrative projects carried on into Properties (hBN, graphene on hBN, graphene with SOC, MoS2, iron, platinum,
// graphene, ceria). Each guide says what the calculations show; "Try this" makes a new project with the model (its
// parameters or a field) or a calculation changed.
import { Accordion, Alert, Anchor, Badge, Button, Card, Group, Loader, SimpleGrid, Stack, Table, Text } from "@mantine/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router";
import { ErrorBox } from "../components/Errors";
import { PageTitle } from "../components/Layout";
import { Markdown } from "../components/Markdown";
import { useMe } from "../lib/hooks";
import { openExample, PROPERTIES_EXAMPLES_TITLE, tryExample, useExample, useExamples, type Example, type ExampleTry } from "../lib/examples";
import { propertiesRegistry, type PropertiesMemory } from "../lib/properties";
import { Specialities } from "./Examples";
import { examplesPath } from "../lib/modules";

function useCalcTitles(): (id: string) => string {
  const reg = useQuery({ queryKey: ["properties-registry", "examples"], queryFn: () => propertiesRegistry(), staleTime: 60_000 });
  return (id) => reg.data?.calculators.find((c) => c.id === id)?.title ?? id;
}

export function PropertiesExamplesPage() {
  const { data: all, error, isLoading } = useExamples();
  const data = all?.filter((e) => e.product === "properties");
  const nav = useNavigate();
  return (
    <>
      <PageTitle title={PROPERTIES_EXAMPLES_TITLE} />
      <Text size="sm" c="dimmed" mb="md" maw={900}>
        Ready-made Properties projects. Textbook models - the Haldane and Kane-Mele models, graphene with fields, the MoS₂
        three-band model, a cosine band - show Chern numbers, quantised Hall conductivities and valley Berry curvature, and
        how they change with the model's parameters and with fields. The fits of the Tight Binding illustrative projects carry
        on here with the Berry-phase family: valley curvature in hBN, the anomalous Hall effect of iron, the spin Hall
        effect of platinum and of graphene with spin-orbit coupling. Open one: the model is ready and the calculations are
        set up; "Try this" makes a new project with one thing changed.
      </Text>
      <ErrorBox error={error} />
      {isLoading && <Loader size="sm" />}
      {data && data.length === 0 && <Text size="sm" c="dimmed">None on this server yet.</Text>}
      {data && data.length > 0 && (
        <Card withBorder p={0}>
          <Table.ScrollContainer minWidth={760}>
            <Table highlightOnHover verticalSpacing="sm" data-testid="properties-examples-table">
              <Table.Thead>
                <Table.Tr><Table.Th>#</Table.Th><Table.Th>Project</Table.Th><Table.Th>Model</Table.Th><Table.Th>What it shows</Table.Th>
                  <Table.Th>Where the model comes from</Table.Th></Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.map((e, i) => (
                  <Table.Tr key={e.id} style={{ cursor: "pointer" }} onClick={() => nav(examplesPath("properties", e.id))}>
                    <Table.Td>{i + 1}</Table.Td>
                    <Table.Td><Link to={examplesPath("properties", e.id)} onClick={(ev) => ev.stopPropagation()}><Text fw={600} size="sm">{e.name}</Text></Link>
                      {e.project_id && <Badge size="xs" color="teal" variant="light" ml={6}>opened</Badge>}
                      {e.counterpart && <Badge size="xs" color="blue" variant="light" ml={6}>also in Tight Binding</Badge>}
                      {e.advanced && <Badge size="xs" color="grape" variant="light" ml={6}>Advanced resources</Badge>}</Table.Td>
                    <Table.Td><Text size="sm">{e.material}</Text></Table.Td>
                    <Table.Td><Specialities items={e.speciality} /></Table.Td>
                    <Table.Td><Text size="xs" c="dimmed">{e.model_source}</Text></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>
      )}
    </>
  );
}

/** "Try this" for a Properties illustrative project: each makes a new Properties project with one thing changed. */
export function PropertiesTryList({ exampleId, tries }: { exampleId: string; tries: ExampleTry[] }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const make = useMutation({
    mutationFn: (t: string) => tryExample(exampleId, t),
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["me"] });
      nav(`/projects/${p.id}`);
    },
  });
  if (!tries.length) return null;
  return (
    <Stack gap="xs" data-testid="properties-try-list">
      <Text fw={600}>Try this</Text>
      <Text size="xs" c="dimmed">Each makes a new Properties project with the model or a calculation changed. Run it and compare
        with this project's result.</Text>
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xs">
        {tries.map((t) => (
          <Card key={t.id} withBorder p="xs">
            <Stack gap={4}>
              <Text size="sm" fw={600}>{t.title}</Text>
              <Text size="xs">{t.about}</Text>
              <Group><Button size="compact-xs" variant="light" loading={make.isPending && make.variables === t.id}
                onClick={() => make.mutate(t.id)} aria-label={`try ${t.id}`}>Make this project</Button></Group>
            </Stack>
          </Card>
        ))}
      </SimpleGrid>
      <ErrorBox error={make.error} />
    </Stack>
  );
}

function OpenButton({ ex }: { ex: Example }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const { data: me } = useMe();
  const allowed = !!me?.limits["properties.enabled"];
  const open = useMutation({
    mutationFn: () => openExample(ex.id),
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["examples"] });
      nav(`/projects/${p.id}`);
    },
  });
  return (
    <Stack gap={4} align="flex-start">
      <Button onClick={() => open.mutate()} loading={open.isPending} disabled={!allowed} data-testid="open-properties-example">
        {ex.project_id ? "Continue in my project" : "Open as my project"}</Button>
      {!allowed && <Text size="xs" c="orange">Properties is not part of your plan.</Text>}
      <ErrorBox error={open.error} />
    </Stack>
  );
}

/** The guide's calculations, in order, with what to look for in each. */
export function CalculationTable({ ex }: { ex: Example }) {
  const title = useCalcTitles();
  return (
    <Table fz="sm" withRowBorders={false} data-testid="properties-example-steps">
      <Table.Tbody>
        {(ex.calculations ?? []).map((c, i) => (
          <Table.Tr key={c.calculator}><Table.Td w={36}><Text size="sm" fw={600}>{i + 1}</Text></Table.Td>
            <Table.Td w={260}><Text size="sm" fw={600}>{title(c.calculator)}</Text></Table.Td><Table.Td><Text size="sm">{c.note}</Text></Table.Td></Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

export function PropertiesExampleDetail() {
  const { id } = useParams();
  const { data: ex, error, isLoading } = useExample(id);
  const { data: mat } = useExample(ex?.counterpart ?? undefined);
  if (isLoading) return <Loader size="sm" />;
  if (error || !ex) return <ErrorBox error={error ?? new Error("Not found")} />;
  return (
    <>
      <PageTitle title={ex.name}>
        <Button component={Link} to={examplesPath("properties")} variant="default">All Properties illustrative projects</Button>
      </PageTitle>
      <Stack gap="md" maw={1000}>
        <Card withBorder>
          <Stack gap={6}>
            <Text size="sm"><b>Model:</b> {ex.material}</Text>
            <Text size="sm"><b>Where it comes from:</b> {ex.model_source}</Text>
            <Specialities items={ex.speciality} />
            <Text size="sm" c="dimmed">{ex.summary}</Text>
            {ex.expect && <Alert color="teal" p="xs" data-testid="properties-example-expect"><Text size="sm"><b>What it shows:</b> {ex.expect}</Text></Alert>}
            {ex.counterpart && (
              <Text size="sm" data-testid="properties-example-counterpart">The model is the fit of the Tight Binding illustrative project{" "}
                <Anchor component={Link} to={examplesPath("tight-binding", ex.counterpart)}>{mat?.name ?? ex.counterpart}</Anchor>: fit it there yourself and
                press <i>Continue in Properties</i> on its step 4 to carry on with your own fit.</Text>
            )}
            <OpenButton ex={ex} />
            {ex.advanced && <Alert color="grape" p="xs"><Text size="sm">This project runs with the Advanced plan's Properties limits
              (the Berry-phase family, larger meshes), granted by an administrator.</Text></Alert>}
          </Stack>
        </Card>
        <Card withBorder><Text fw={600} mb={4}>The calculations</Text><CalculationTable ex={ex} /></Card>
        <Card withBorder data-testid="example-guide"><Markdown source={ex.guide ?? ""} /></Card>
        <Card withBorder><PropertiesTryList exampleId={ex.id} tries={ex.tries ?? []} /></Card>
      </Stack>
    </>
  );
}

/** The banner of a Properties project opened from an illustrative project, or made by its "try this". */
export function PropertiesExampleBanner({ project, mem }: { project: { settings?: Record<string, unknown> | null }; mem: PropertiesMemory }) {
  const st = (project.settings ?? {}) as { example?: string };
  const from = mem.from_example;
  const id = st.example ?? from?.id;
  const { data: ex } = useExample(id);
  if (!id || (!st.example && !from)) return null;
  if (from) {
    return (
      <Alert color="grape" p="xs" mb="sm" data-testid="properties-from-example">
        <Text size="sm">Made from the illustrative project <Anchor component={Link} to={examplesPath("properties", from.id)}>{ex?.name ?? from.id}</Anchor>:
          {" "}<b>{from.title}</b> - {from.about}</Text>
        <Text size="xs" c="dimmed">Run it and compare with the illustrative project{ex?.expect ? ` (${ex.expect.replace(/\.$/, "")})` : ""}.</Text>
      </Alert>
    );
  }
  return (
    <Card withBorder p="sm" mb="sm" data-testid="properties-example-banner">
      <Stack gap={6}>
        <Text size="sm"><b>Illustrative project</b>{ex ? `: ${ex.name}` : ""}. The model is ready and the calculations are set up:
          run them in order and compare with the guide. <Anchor component={Link} to={examplesPath("properties", id)} size="sm">All about it</Anchor></Text>
        {ex?.expect && <Text size="xs" c="dimmed"><b>What it shows:</b> {ex.expect}</Text>}
        {ex && <Accordion variant="contained" chevronPosition="left">
          <Accordion.Item value="guide">
            <Accordion.Control><Text size="sm">Guide: what to look for and the subtleties</Text></Accordion.Control>
            <Accordion.Panel><Markdown source={ex.guide ?? ""} /></Accordion.Panel>
          </Accordion.Item>
          <Accordion.Item value="try">
            <Accordion.Control><Text size="sm">Try this ({ex.tries?.length ?? 0})</Text></Accordion.Control>
            <Accordion.Panel><PropertiesTryList exampleId={ex.id} tries={ex.tries ?? []} /></Accordion.Panel>
          </Accordion.Item>
        </Accordion>}
      </Stack>
    </Card>
  );
}
